import type { PolygonOverlayPoint } from "../types";
import type { GetPolygonLabelPoint, LabelCell } from "./types";

const DEFAULT_PRECISION = 1;

const squaredDistanceToSegment = (
  point: PolygonOverlayPoint,
  segmentStart: PolygonOverlayPoint,
  segmentEnd: PolygonOverlayPoint,
): number => {
  const segmentDeltaX = segmentEnd.x - segmentStart.x;
  const segmentDeltaY = segmentEnd.y - segmentStart.y;
  const segmentLengthSquared =
    segmentDeltaX * segmentDeltaX + segmentDeltaY * segmentDeltaY;

  // Where the point projects onto the segment, clamped to its ends
  const projection =
    segmentLengthSquared === 0
      ? 0
      : Math.max(
          0,
          Math.min(
            1,
            ((point.x - segmentStart.x) * segmentDeltaX +
              (point.y - segmentStart.y) * segmentDeltaY) /
              segmentLengthSquared,
          ),
        );

  const nearestX = segmentStart.x + segmentDeltaX * projection;
  const nearestY = segmentStart.y + segmentDeltaY * projection;
  const distanceX = point.x - nearestX;
  const distanceY = point.y - nearestY;
  return distanceX * distanceX + distanceY * distanceY;
};

const signedDistanceToEdge = (
  point: PolygonOverlayPoint,
  points: PolygonOverlayPoint[],
): number => {
  let isInside = false;
  let minSquaredDistance = Infinity;

  for (
    let index = 0, previousIndex = points.length - 1;
    index < points.length;
    previousIndex = index++
  ) {
    const edgeStart = points[index];
    const edgeEnd = points[previousIndex];

    // Ray casting: a horizontal ray from the point crosses this edge when the
    // edge spans the point's y and the crossing lies to the right of the point
    const isEdgeStartAbove = edgeStart.y > point.y;
    const isEdgeEndAbove = edgeEnd.y > point.y;
    if (isEdgeStartAbove !== isEdgeEndAbove) {
      const crossingX =
        ((edgeEnd.x - edgeStart.x) * (point.y - edgeStart.y)) /
          (edgeEnd.y - edgeStart.y) +
        edgeStart.x;
      if (point.x < crossingX) isInside = !isInside;
    }

    minSquaredDistance = Math.min(
      minSquaredDistance,
      squaredDistanceToSegment(point, edgeStart, edgeEnd),
    );
  }

  return (isInside ? 1 : -1) * Math.sqrt(minSquaredDistance);
};

const createCell = (
  x: number,
  y: number,
  halfSize: number,
  points: PolygonOverlayPoint[],
): LabelCell => {
  const distance = signedDistanceToEdge({ x, y }, points);
  return {
    x,
    y,
    halfSize,
    distance,
    maxDistance: distance + halfSize * Math.SQRT2,
  };
};

const popMostPromisingCell = (queue: LabelCell[]): LabelCell => {
  let bestIndex = 0;
  for (let index = 1; index < queue.length; index++) {
    if (queue[index].maxDistance > queue[bestIndex].maxDistance) {
      bestIndex = index;
    }
  }
  return queue.splice(bestIndex, 1)[0];
};

// The pole of inaccessibility: the point farthest from every edge, so a label
// stays inside even for U- and L-shaped rooms (the polylabel quadtree search)
export const getPolygonLabelPoint: GetPolygonLabelPoint = ({
  points,
  precision = DEFAULT_PRECISION,
}) => {
  if (points.length === 0) return null;

  const xValues = points.map((point) => point.x);
  const yValues = points.map((point) => point.y);
  const minX = Math.min(...xValues);
  const minY = Math.min(...yValues);
  const width = Math.max(...xValues) - minX;
  const height = Math.max(...yValues) - minY;
  const cellSize = Math.min(width, height);

  if (cellSize === 0) {
    return { x: minX + width / 2, y: minY + height / 2 };
  }

  const halfSize = cellSize / 2;
  const queue: LabelCell[] = [];
  for (let x = minX; x < minX + width; x += cellSize) {
    for (let y = minY; y < minY + height; y += cellSize) {
      queue.push(createCell(x + halfSize, y + halfSize, halfSize, points));
    }
  }

  let bestCell = createCell(minX + width / 2, minY + height / 2, 0, points);

  while (queue.length > 0) {
    const cell = popMostPromisingCell(queue);
    if (cell.distance > bestCell.distance) bestCell = cell;
    // No point in this cell can beat the best one by more than the precision
    if (cell.maxDistance - bestCell.distance <= precision) continue;

    const quarterSize = cell.halfSize / 2;
    queue.push(
      createCell(
        cell.x - quarterSize,
        cell.y - quarterSize,
        quarterSize,
        points,
      ),
      createCell(
        cell.x + quarterSize,
        cell.y - quarterSize,
        quarterSize,
        points,
      ),
      createCell(
        cell.x - quarterSize,
        cell.y + quarterSize,
        quarterSize,
        points,
      ),
      createCell(
        cell.x + quarterSize,
        cell.y + quarterSize,
        quarterSize,
        points,
      ),
    );
  }

  return { x: bestCell.x, y: bestCell.y };
};
